import { useEffect, useState } from 'react';
import { Download, Loader2, Plus, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, downloadAuthedFile, formatEur, inputCls, statusBadge } from '../../utils/operations';
import { DatePickerField } from '../DatePickerField';

const emptyPayout = { partner_name: '', gross_amount_eur: '', partner_amount_eur: '', due_date: '', note: '' };

export const AdminPayments = ({ payments, onRefresh }) => {
  const [forms, setForms] = useState({});
  const [busy, setBusy] = useState('');
  const [accounts, setAccounts] = useState([]);
  useEffect(() => {
    apiFetch('/settings').then((s) => setAccounts((s.financial_accounts || []).filter((a) => a.active && a.currency === 'EUR'))).catch(() => setAccounts([]));
  }, []);
  const setForm = (id, key, value) => setForms((f) => ({ ...f, [id]: { method: 'bank_transfer', payment_reference: '', note: '', account_id: '', ...f[id], [key]: value } }));

  const confirm = async (payment) => {
    const form = { method: 'bank_transfer', payment_reference: '', note: '', account_id: '', ...forms[payment.id] };
    setBusy(payment.id);
    try {
      const result = await apiFetch(`/payments/${payment.id}/confirm`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      toast.success('Pagamento confirmado', { description: result.voucher ? `Voucher ${result.voucher.code} emitido automaticamente.` : 'Automação de voucher desligada.' });
      await onRefresh();
    } catch (err) {
      toast.error('Pagamento não confirmado', { description: String(err.message) });
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-payment-list">
      {payments.map((p) => {
        const form = { method: 'bank_transfer', payment_reference: '', note: '', account_id: '', ...forms[p.id] };
        return (
          <article key={p.id} data-testid={`admin-payment-${p.id}`} className="rounded-xl border border-eco-ink/10 bg-white p-5">
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-eco-ink">{p.reference} · {p.traveler_email}</p>
                <p className="mt-1 text-xs text-eco-ink/55">Proposta {p.proposal_id.slice(0, 8)} · snapshot FX guardado{p.account_name ? ` · Conta: ${p.account_name}` : ''}</p>
              </div>
              <p data-testid={`admin-payment-amount-${p.id}`} className="eco-number text-2xl leading-tight font-bold tracking-[-0.02em] text-eco-green">{formatEur(p.amount_eur)}</p>
              <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${statusBadge(p.status)}`}>{p.status}</span>
            </div>
            {p.status === 'pending' && (
              <div className="mt-4 grid gap-3 border-t border-eco-ink/10 pt-4 md:grid-cols-[150px_1fr_1fr_1fr_auto]">
                <select data-testid={`payment-method-${p.id}`} value={form.method} onChange={(e) => setForm(p.id, 'method', e.target.value)} className={inputCls}>
                  <option value="bank_transfer">Transferência</option>
                  <option value="card_external">Cartão externo</option>
                  <option value="cash">Numerário</option>
                  <option value="other">Outro</option>
                </select>
                <select data-testid={`payment-account-${p.id}`} value={form.account_id} onChange={(e) => setForm(p.id, 'account_id', e.target.value)} className={inputCls}>
                  <option value="">Conta…</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
                </select>
                <input data-testid={`payment-reference-${p.id}`} value={form.payment_reference} onChange={(e) => setForm(p.id, 'payment_reference', e.target.value)} placeholder="Referência real do pagamento" className={inputCls} />
                <input data-testid={`payment-note-${p.id}`} value={form.note} onChange={(e) => setForm(p.id, 'note', e.target.value)} placeholder="Nota interna" className={inputCls} />
                <button data-testid={`payment-confirm-${p.id}`} onClick={() => confirm(p)} disabled={busy === p.id} className="eco-btn-primary justify-center disabled:opacity-60">
                  {busy === p.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Confirmar pagamento
                </button>
              </div>
            )}
            {p.status === 'paid' && <p className="mt-3 text-xs text-eco-ink/50">Pago via {p.method}{p.payment_reference && ` · ${p.payment_reference}`}{p.paid_at && ` · ${new Date(p.paid_at).toLocaleString('pt-PT')}`}</p>}
          </article>
        );
      })}
      {payments.length === 0 && <p className="rounded-xl border border-dashed border-eco-ink/15 bg-white py-12 text-center text-sm text-eco-ink/50">Sem pagamentos manuais registados.</p>}
    </div>
  );
};

export const AdminPayouts = ({ payouts, partners, onRefresh }) => {
  const [form, setForm] = useState(emptyPayout);
  const [references, setReferences] = useState({});
  const [busy, setBusy] = useState(false);

  const createManual = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiFetch('/payouts', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, gross_amount_eur: Number(form.gross_amount_eur || 0), partner_amount_eur: Number(form.partner_amount_eur || 0) }),
      });
      toast.success('Entrada de payout criada');
      setForm(emptyPayout);
      await onRefresh();
    } catch (err) {
      toast.error('Payout não criado', { description: String(err.message) });
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async (payout) => {
    try {
      await apiFetch(`/payouts/${payout.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'paid', payment_reference: references[payout.id] || payout.payment_reference || '' }) });
      toast.success('Payout marcado como pago');
      await onRefresh();
    } catch (err) {
      toast.error('Payout não atualizado', { description: String(err.message) });
    }
  };

  return (
    <div data-testid="admin-payout-ledger">
      <form data-testid="manual-payout-form" onSubmit={createManual} className="grid gap-3 rounded-xl border border-eco-ink/10 bg-white p-5 md:grid-cols-6">
        <select data-testid="manual-payout-partner" required value={form.partner_name} onChange={(e) => setForm({ ...form, partner_name: e.target.value })} className={inputCls}>
          <option value="">Parceiro…</option>
          {partners.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
        </select>
        <input data-testid="manual-payout-gross" type="number" step="0.01" min="0" value={form.gross_amount_eur} onChange={(e) => setForm({ ...form, gross_amount_eur: e.target.value })} placeholder="Bruto EUR" className={inputCls} />
        <input data-testid="manual-payout-net" required type="number" step="0.01" min="0" value={form.partner_amount_eur} onChange={(e) => setForm({ ...form, partner_amount_eur: e.target.value })} placeholder="Parceiro EUR" className={inputCls} />
        <DatePickerField testid="manual-payout-due" value={form.due_date} onChange={(value) => setForm({ ...form, due_date: value })} placeholder="Due date" />
        <input data-testid="manual-payout-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Nota / ajuste" className={inputCls} />
        <button data-testid="manual-payout-submit" disabled={busy} className="eco-btn-primary justify-center"><Plus size={14} /> Adicionar</button>
      </form>

      <div className="mt-5 flex justify-end">
        <button type="button" data-testid="payout-export" onClick={async () => { try { await downloadAuthedFile('/payouts/export.csv', 'payouts.csv'); toast.success('Export descarregado'); } catch (e) { toast.error('Export falhou', { description: String(e.message) }); } }} className="eco-btn-secondary"><Download size={14} /> Export CSV</button>
      </div>

      <div className="mt-5 space-y-3">
        {payouts.map((p) => (
          <article key={p.id} data-testid={`admin-payout-${p.id}`} className="rounded-xl border border-eco-ink/10 bg-white p-5">
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-eco-ink">{p.partner_name}</p>
                <p className="mt-1 text-xs text-eco-ink/55">{p.voucher_code || p.request_id || 'Ajuste manual'} · due {p.due_date || '—'} · provider {p.provider}</p>
              </div>
              <div className="text-right text-xs text-eco-ink/55">
                <p>Bruto {formatEur(p.gross_amount_eur)} · Margem {formatEur(p.eco_margin_eur)}</p>
                <p data-testid={`payout-amount-${p.id}`} className="eco-number text-2xl leading-tight font-bold tracking-[-0.02em] text-eco-green">{formatEur(p.partner_amount_eur)}</p>
              </div>
              <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${statusBadge(p.status)}`}>{p.status}</span>
            </div>
            {p.status !== 'paid' && (
              <div className="mt-4 flex flex-wrap gap-2 border-t border-eco-ink/10 pt-4">
                <input data-testid={`payout-reference-${p.id}`} value={references[p.id] ?? p.payment_reference ?? ''} onChange={(e) => setReferences({ ...references, [p.id]: e.target.value })} placeholder="Referência de pagamento real" className={`${inputCls} max-w-sm`} />
                <button data-testid={`payout-mark-paid-${p.id}`} onClick={() => markPaid(p)} className="inline-flex min-h-10 items-center rounded-lg bg-eco-green px-4 text-xs font-semibold text-white transition-colors hover:bg-eco-deep">Marcar pago</button>
              </div>
            )}
          </article>
        ))}
        {payouts.length === 0 && <p className="rounded-xl border border-dashed border-eco-ink/15 bg-white py-12 text-center text-sm text-eco-ink/50">Sem payouts no ledger.</p>}
      </div>
    </div>
  );
};
